/**
 * Probabilités calculées CÔTÉ SERVEUR (moteur Elo + Monte Carlo), poussées via
 * l'event SSE `derived`. Quand elles arrivent, le navigateur les affiche
 * directement au lieu de relancer son propre worker. Sur un hébergement
 * statique (pas de serveur), elles n'arrivent jamais et le worker prend le
 * relais — l'app reste autonome.
 */
import type { SimulationOutput } from "../engine/montecarlo.ts";

export interface DerivedProbabilities {
  readonly finishedCount: number;
  readonly nSims: number;
  readonly seed: string;
  readonly computedAt: string;
  readonly titleProb: Record<string, number>;
  readonly finalProb: Record<string, number>;
  readonly semiProb: Record<string, number>;
  readonly qualifyProb: Record<string, number>;
  readonly slotDistribution: SimulationOutput["slotDistribution"];
}

function isProbMap(v: unknown): v is Record<string, number> {
  return (
    typeof v === "object" &&
    v !== null &&
    !Array.isArray(v) &&
    // chaque valeur est une probabilité finie dans [0,1] (tolérance d'arrondi)
    Object.values(v).every((n) => typeof n === "number" && Number.isFinite(n) && n >= 0 && n <= 1.0001)
  );
}

/** Validation légère de l'event SSE `derived` (rejette en bloc si douteux). */
export function parseDerived(raw: unknown): DerivedProbabilities | null {
  if (typeof raw !== "object" || raw === null) return null;
  const d = raw as Record<string, unknown>;
  if (typeof d.finishedCount !== "number" || d.finishedCount < 0 || typeof d.nSims !== "number" || d.nSims <= 0) {
    return null;
  }
  if (!isProbMap(d.titleProb) || !isProbMap(d.finalProb) || !isProbMap(d.semiProb) || !isProbMap(d.qualifyProb)) {
    return null;
  }
  if (typeof d.slotDistribution !== "object" || d.slotDistribution === null) return null;
  return {
    finishedCount: d.finishedCount,
    nSims: d.nSims,
    seed: typeof d.seed === "string" ? d.seed : typeof d.seed === "number" ? String(d.seed) : "",
    computedAt: typeof d.computedAt === "string" ? d.computedAt : new Date().toISOString(),
    titleProb: d.titleProb,
    finalProb: d.finalProb,
    semiProb: d.semiProb,
    qualifyProb: d.qualifyProb,
    slotDistribution: d.slotDistribution as SimulationOutput["slotDistribution"],
  };
}

/** Adapte les probas serveur à la forme SimulationOutput attendue par l'UI. */
export function derivedToSimulationOutput(d: DerivedProbabilities): SimulationOutput {
  return {
    nSims: d.nSims,
    seed: d.seed,
    titleProb: d.titleProb,
    finalProb: d.finalProb,
    semiProb: d.semiProb,
    qualifyProb: d.qualifyProb,
    slotDistribution: d.slotDistribution,
  };
}
